import { ConfigProvider, Grid, Typography } from 'antd';
import type { TableProps } from 'antd';
import React from 'react';
import { generatePath, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import generateColumns from './Columns';
import ExpandableRow from './ExpandableRow';
import ExpandIcon from './ExpandIcon';
import Footer from './Footer';
import useLeaderboard, { type LeaderboardRow } from './hooks/useLeaderboard';
import LeaderboardSkeleton from './LeaderboardSkeleton';
import LeaderboardTable from './LeaderboardTable';

const { Title } = Typography;

const { useBreakpoint } = Grid;

const LeaderboardTitle = styled(Title)`
  padding-top: 24px;
  margin: 0;
`;

const renderTableFooter = (partnerId: string, showStyleColumn: boolean) => (
  <Footer showStyleColumn={showStyleColumn} partnerId={partnerId} />
);

type ExpandedRowRender = NonNullable<
  TableProps<LeaderboardRow>['expandable']
>['expandedRowRender'];

type ExpandIconRender = NonNullable<
  TableProps<LeaderboardRow>['expandable']
>['expandIcon'];

const Leaderboard = function () {
  const { md } = useBreakpoint();
  const { partnerId } = useParams();
  const [searchParams] = useSearchParams();
  const expertId = searchParams.get('id')!;
  const isSmallDevice = !md;

  const { loading, leaderboard, showStyleColumn } = useLeaderboard(partnerId!, {
    expertId,
  });

  const getRowClassName = (row: LeaderboardRow) => {
    if ('node' in row) {
      return row.node.externalId === expertId ? 'highlight-guuru-row' : '';
    }
    return '';
  };

  const getRowKey = (row: LeaderboardRow) => {
    if ('node' in row) {
      return row.node.externalId;
    }
    return row.id;
  };

  const onRow = (row: LeaderboardRow) => {
    if ('node' in row) {
      return {
        onClick: () => {
          const path = generatePath('./guurus/:id', {
            id: row.node.externalId,
          });
          window.open(path, '_blank');
        },
      };
    }

    return { onClick: () => {} };
  };

  const renderExpandedRow: ExpandedRowRender = (row) =>
    'node' in row ? <ExpandableRow node={row.node} /> : null;

  const renderExpandIcon: ExpandIconRender = (props) => (
    <ExpandIcon {...props} />
  );

  return (
    <>
      <LeaderboardTitle level={2}>Community Top Ten</LeaderboardTitle>
      {loading ? (
        <LeaderboardSkeleton />
      ) : (
        <ConfigProvider
          theme={{
            components: {
              Table: {
                rowExpandedBg: '#FAFAFA',
                headerBg: '#fff',
                headerColor: 'rgb(107 114 128)',
                headerBorderRadius: 6,
                headerSplitColor: '#fff',
                cellPaddingBlock: 12,
                rowHoverBg: '#E6F7FF',
                cellFontSize: 16,
              },
            },
          }}
        >
          <LeaderboardTable
            pagination={false}
            columns={generateColumns(isSmallDevice, showStyleColumn)}
            rowHoverable
            rowClassName={(row) => getRowClassName(row)}
            dataSource={leaderboard}
            footer={() => renderTableFooter(partnerId!, showStyleColumn)}
            rowKey={(row) => getRowKey(row)}
            onRow={(row) => onRow(row)}
            expandable={
              isSmallDevice
                ? {
                    expandedRowRender: renderExpandedRow,
                    expandIcon: renderExpandIcon,
                  }
                : {}
            }
          />
        </ConfigProvider>
      )}
    </>
  );
};

export default Leaderboard;
